import { ExternalLinkIcon, PlusIcon } from "@radix-ui/react-icons";
import { AlertDialog, Box, Button, Card, Dialog, Flex, Heading, Text, VisuallyHidden } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import chatgptIcon from "../../assets/chatgpt-icon.png";
import claudeIcon from "../../assets/claude-icon.png";
import DashboardShell from "../../components/dashboard/DashboardShell";
import InterestLine from "../../components/dashboard/InterestLine";
import { CARD_NO_PADDING, MONO_STYLE, statusBadgeStyle } from "../../components/dashboard/tokens";
import RemoteSetupFlow from "../../components/onboarding/RemoteSetupFlow";
import LoadingIndicator from "../../components/shared/LoadingIndicator";
import { CHATGPT_CONNECTOR_URL, CLAUDE_CONNECTOR_URL, MCP_CONNECTOR_URL } from "../../consts";
import { useAuth } from "../../contexts/AuthContext";
import { usePostHog } from "../../hooks/usePostHog";
import { type Device, useUserDevices } from "../../hooks/useUserDevices";
import { deviceStatusCategory, deviceStatusLabel } from "../../utils/deviceStatus";
import { copyToClipboard, formatRelativeTime } from "../../utils/ui";

const AI_CLIENTS = [
    {
        name: "ChatGPT",
        description: "Open the Desktop Commander app in ChatGPT.",
        href: CHATGPT_CONNECTOR_URL,
        iconSrc: chatgptIcon,
    },
    {
        name: "Claude",
        description: "Open the Desktop Commander connector in Claude.",
        href: CLAUDE_CONNECTOR_URL,
        iconSrc: claudeIcon,
    },
];

const CLIENT_ICON_SIZE = 36;

function ClientIcon({ src, label }: { src?: string; label?: string }) {
    if (src) {
        return (
            <img
                src={src}
                alt=""
                style={{
                    width: CLIENT_ICON_SIZE,
                    height: CLIENT_ICON_SIZE,
                    borderRadius: "9px",
                    flex: "0 0 auto",
                    objectFit: "cover",
                }}
            />
        );
    }
    return (
        <Flex
            align="center"
            justify="center"
            aria-hidden="true"
            style={{
                width: CLIENT_ICON_SIZE,
                height: CLIENT_ICON_SIZE,
                borderRadius: "9px",
                backgroundColor: "var(--dc-raise-bg)",
                color: "var(--dc-muted)",
                flex: "0 0 auto",
                fontFamily: "var(--dc-font-mono)",
                fontSize: "10px",
                fontWeight: 600,
            }}
        >
            {label}
        </Flex>
    );
}

const STATUS_DOT_COLOR: Record<ReturnType<typeof deviceStatusCategory>, string> = {
    success: "var(--dc-success)",
    warning: "var(--dc-amber)",
    neutral: "var(--dc-muted-2)",
};

const DevicesPage = () => {
    const { devices, loading, revokeDevice, revokeAllDevices, revokingId, revokingAll } = useUserDevices();
    const [addDeviceOpen, setAddDeviceOpen] = useState(false);
    const [universalClientOpen, setUniversalClientOpen] = useState(false);
    const [revokeError, setRevokeError] = useState<string | null>(null);
    const handleRevoke = async (deviceId: string) => {
        const result = await revokeDevice(deviceId);
        setRevokeError(result.success ? null : result.message);
    };
    const handleRevokeAll = async () => {
        const result = await revokeAllDevices();
        setRevokeError(result.success ? null : result.message);
    };
    const { user } = useAuth();
    const { track, trackOnce } = usePostHog();

    // Offline rows offer Reconnect and Remove; online rows offer Revoke. Both remove the device the same way.
    const isOffline = (device: Device) => deviceStatusCategory(device.status) === "neutral";
    const removeDeviceDialog = (device: Device) => {
        const offline = isOffline(device);
        const [verb, busyLabel, outcome] = offline
            ? [
                  "Remove",
                  "Removing…",
                  "It will be removed from your list and signed out.",
              ]
            : [
                  "Revoke",
                  "Revoking…",
                  "It will be disconnected now, removed from your list and signed out.",
              ];
        return (
            <AlertDialog.Root>
                <AlertDialog.Trigger>
                    <Button size="1" color="red" variant={offline ? "ghost" : "surface"} disabled={revokingId === device.id || revokingAll}>
                        {revokingId === device.id ? busyLabel : verb}
                    </Button>
                </AlertDialog.Trigger>
                <AlertDialog.Content maxWidth="450px">
                    <AlertDialog.Title>
                        {verb} "{device.device_name}"?
                    </AlertDialog.Title>
                    <AlertDialog.Description size="2">{outcome} To use it again, run the setup command on it and sign in.</AlertDialog.Description>
                    <Flex gap="3" mt="4" justify="end">
                        <AlertDialog.Cancel>
                            <Button variant="soft" color="gray">
                                Cancel
                            </Button>
                        </AlertDialog.Cancel>
                        <AlertDialog.Action>
                            <Button variant="solid" color="red" onClick={() => handleRevoke(device.id)}>
                                {verb}
                            </Button>
                        </AlertDialog.Action>
                    </Flex>
                </AlertDialog.Content>
            </AlertDialog.Root>
        );
    };

    // Reconnect reuses the add device flow for one named offline device. It closes itself once that
    // device is back online, a new record appears (the computer signed in as a new device), or the
    // record is gone (removed in another tab).
    const [reconnectDevice, setReconnectDevice] = useState<Device | null>(null);
    const deviceCountAtReconnect = useRef(0);
    const openReconnect = (device: Device) => {
        deviceCountAtReconnect.current = devices?.length ?? 0;
        setReconnectDevice(device);
        void track("device_reconnect_opened", {
            user_id: user?.id,
            device_id: device.id,
        });
    };
    useEffect(() => {
        if (!reconnectDevice) {
            return;
        }
        const current = devices?.find((device) => device.id === reconnectDevice.id);
        if (!current || current.status === "online" || (devices?.length ?? 0) > deviceCountAtReconnect.current) {
            setReconnectDevice(null);
        }
    }, [
        devices,
        reconnectDevice,
    ]);
    const hasTrackedView = useRef(false);

    // This is the dashboard's landing page (root route), same as McpDashboardView
    // was before — carrying its page-view event forward rather than letting it
    // silently stop firing.
    useEffect(() => {
        if (!loading && user?.id && !hasTrackedView.current) {
            hasTrackedView.current = true;
            const onlineDeviceCount = devices?.filter((d) => d.status === "online").length ?? 0;
            trackOnce("page_user_dashboard_viewed", user.id, {
                user_id: user.id,
                email: user.email,
                device_count: devices?.length ?? 0,
                online_device_count: onlineDeviceCount,
            });
        }
    }, [
        loading,
        user?.id,
        user?.email,
        devices,
        trackOnce,
    ]);

    // The device list refreshes from the server every 15s (see SupabaseContext);
    // a newly-verified device shows up as a count increase within that window.
    // Closing the dialog then, instead of leaving the user to notice and close
    // it themselves, is the whole point of "this list updates automatically."
    const deviceCountAtOpen = useRef<number | null>(null);
    useEffect(() => {
        // Only the open/close transition should reset the baseline — see the
        // effect below for what happens as the count changes while it's open.
        deviceCountAtOpen.current = addDeviceOpen ? (devices?.length ?? 0) : null;
    }, [
        addDeviceOpen,
    ]);
    useEffect(() => {
        if (deviceCountAtOpen.current !== null && (devices?.length ?? 0) > deviceCountAtOpen.current) {
            setAddDeviceOpen(false);
        }
    }, [
        devices?.length,
    ]);

    const addDeviceDialog = (
        <Dialog.Root open={addDeviceOpen} onOpenChange={setAddDeviceOpen}>
            <Dialog.Trigger>
                <Button size="3">
                    <PlusIcon aria-hidden="true" />
                    Add a device
                </Button>
            </Dialog.Trigger>
            {/* 560px matches the OAuth setup screen, so the same flow wraps its lines the same way.
                The title is for screen readers only: the flow's own heading is the visible one, and
                two headings saying the same thing is exactly the drift we are avoiding. */}
            <Dialog.Content maxWidth="560px">
                <VisuallyHidden>
                    <Dialog.Title>Add a device</Dialog.Title>
                </VisuallyHidden>
                <RemoteSetupFlow
                    source="devices_page"
                    userId={user?.id}
                    deviceCount={devices?.length ?? 0}
                    copyCardFooter={<InterestLine line="hosted_server" source="add_device_dialog" />}
                />
            </Dialog.Content>
        </Dialog.Root>
    );

    // With no devices the card itself already shows the connect instructions, so the
    // header button would just open a dialog saying the same thing.
    const hasDevices = Boolean(devices && devices.length > 0);

    return (
        <DashboardShell
            title="Devices"
            subtitle="Machines your AI clients can reach through Remote MCP."
            action={hasDevices ? addDeviceDialog : undefined}
        >
            <Dialog.Root open={reconnectDevice !== null} onOpenChange={(open) => !open && setReconnectDevice(null)}>
                <Dialog.Content maxWidth="560px">
                    <VisuallyHidden>
                        <Dialog.Title>Reconnect {reconnectDevice?.device_name}</Dialog.Title>
                    </VisuallyHidden>
                    {reconnectDevice && (
                        <RemoteSetupFlow
                            source="device_reconnect"
                            userId={user?.id}
                            deviceCount={devices?.length ?? 0}
                            reconnectDeviceName={reconnectDevice.device_name}
                        />
                    )}
                </Dialog.Content>
            </Dialog.Root>
            <Flex direction="column" gap="5">
                <Card size="3" style={CARD_NO_PADDING}>
                    <Flex justify="between" align="center" px="5" py="4">
                        <Heading size="3">Your devices</Heading>
                        {hasDevices && (
                            <AlertDialog.Root>
                                <AlertDialog.Trigger>
                                    <Button size="1" color="red" variant="surface" disabled={revokingAll}>
                                        {revokingAll ? "Revoking…" : "Revoke all"}
                                    </Button>
                                </AlertDialog.Trigger>
                                <AlertDialog.Content maxWidth="450px">
                                    <AlertDialog.Title>Revoke all devices</AlertDialog.Title>
                                    <AlertDialog.Description size="2">
                                        This disconnects every device from Remote MCP. This cannot be undone.
                                    </AlertDialog.Description>
                                    <Flex gap="3" mt="4" justify="end">
                                        <AlertDialog.Cancel>
                                            <Button variant="soft" color="gray">
                                                Cancel
                                            </Button>
                                        </AlertDialog.Cancel>
                                        <AlertDialog.Action>
                                            <Button variant="solid" color="red" onClick={handleRevokeAll}>
                                                Revoke all
                                            </Button>
                                        </AlertDialog.Action>
                                    </Flex>
                                </AlertDialog.Content>
                            </AlertDialog.Root>
                        )}
                    </Flex>

                    {revokeError && (
                        <Box px="5" pb="3">
                            <Text size="2" color="red" role="alert">
                                Something went wrong: {revokeError}
                            </Text>
                        </Box>
                    )}

                    {loading ? (
                        <Box px="5" pb="4">
                            <LoadingIndicator text="Loading devices…" />
                        </Box>
                    ) : !hasDevices ? (
                        // No devices yet: show the same connect instructions the "Add a device"
                        // dialog shows, inline, rather than an empty list the user has to click
                        // through a dialog to act on.
                        <Box
                            px="5"
                            pb="5"
                            style={{
                                borderTop: "1px solid var(--dc-card-border)",
                            }}
                            pt="5"
                        >
                            {/* No intro line of its own: the flow opens with its own heading and
                                lede, and they are the wording we settled on. */}
                            <RemoteSetupFlow
                                source="devices_page"
                                userId={user?.id}
                                deviceCount={devices?.length ?? 0}
                                copyCardFooter={<InterestLine line="hosted_server" source="devices_empty_state" />}
                            />
                        </Box>
                    ) : (
                        devices?.map((device) => (
                            <Flex key={device.id} align="center" justify="between" gap="3" className="dc-row dc-device-row">
                                <Flex
                                    align="center"
                                    gap="3"
                                    style={{
                                        minWidth: 0,
                                    }}
                                >
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            flex: "0 0 auto",
                                            width: 9,
                                            height: 9,
                                            borderRadius: "50%",
                                            backgroundColor: STATUS_DOT_COLOR[deviceStatusCategory(device.status)],
                                        }}
                                    />
                                    <Box
                                        style={{
                                            minWidth: 0,
                                        }}
                                    >
                                        <Text
                                            weight="medium"
                                            size="2"
                                            truncate
                                            style={{
                                                display: "block",
                                                ...MONO_STYLE,
                                            }}
                                        >
                                            {device.device_name}
                                        </Text>
                                        {/* Online rows carry the badge only: "last seen 2 minutes ago" beside Online reads as a contradiction. */}
                                        {isOffline(device) && (
                                            <Text as="div" size="1" color="gray" title={new Date(device.last_seen).toLocaleString()}>
                                                Last seen {formatRelativeTime(device.last_seen)}
                                            </Text>
                                        )}
                                    </Box>
                                </Flex>
                                <Flex
                                    className="dc-device-actions"
                                    align="center"
                                    gap="3"
                                    style={{
                                        flex: "0 0 auto",
                                    }}
                                >
                                    <span style={statusBadgeStyle(deviceStatusCategory(device.status))}>{deviceStatusLabel(device.status)}</span>
                                    {isOffline(device) ? (
                                        <Flex align="center" gap="3">
                                            <Button size="1" variant="soft" onClick={() => openReconnect(device)}>
                                                Reconnect
                                            </Button>
                                            {removeDeviceDialog(device)}
                                        </Flex>
                                    ) : (
                                        removeDeviceDialog(device)
                                    )}
                                </Flex>
                            </Flex>
                        ))
                    )}
                    {!loading && hasDevices && <InterestLine line="device_share" source="devices_page" />}
                </Card>

                <Card size="3" style={CARD_NO_PADDING}>
                    <Box px="5" pt="4" pb="4">
                        <Heading size="3" mb="1">
                            Where you use it
                        </Heading>
                        <Text as="p" size="2" color="gray">
                            Open Desktop Commander in your assistant. Any MCP client can use the same tunnel.
                        </Text>
                    </Box>

                    {AI_CLIENTS.map((client) => (
                        <a
                            key={client.name}
                            href={client.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: "block",
                                color: "inherit",
                                textDecoration: "none",
                            }}
                        >
                            <Flex align="center" justify="between" gap="3" className="dc-row dc-row--interactive dc-client-row">
                                <Flex
                                    align="center"
                                    gap="3"
                                    style={{
                                        minWidth: 0,
                                    }}
                                >
                                    <ClientIcon src={client.iconSrc} />
                                    <Box>
                                        <Text
                                            weight="medium"
                                            size="2"
                                            style={{
                                                display: "block",
                                            }}
                                        >
                                            {client.name}
                                        </Text>
                                        <Text as="div" size="1" color="gray">
                                            {client.description}
                                        </Text>
                                    </Box>
                                </Flex>
                                {/* Plain text, not a nested <Link> — the whole row is already the <a>. */}
                                <Flex
                                    align="center"
                                    gap="1"
                                    style={{
                                        color: "var(--blue-9)",
                                        fontSize: "var(--font-size-2)",
                                        flex: "0 0 auto",
                                    }}
                                >
                                    Open
                                    <ExternalLinkIcon aria-hidden="true" />
                                </Flex>
                            </Flex>
                        </a>
                    ))}

                    <button
                        type="button"
                        className="dc-row dc-row--interactive"
                        aria-expanded={universalClientOpen}
                        onClick={() => setUniversalClientOpen((open) => !open)}
                        style={{
                            width: "100%",
                            border: 0,
                            background: "transparent",
                            color: "inherit",
                            font: "inherit",
                            textAlign: "left",
                            cursor: "pointer",
                        }}
                    >
                        <Flex
                            align="center"
                            justify="between"
                            gap="3"
                            style={{
                                width: "100%",
                            }}
                        >
                            <Flex align="center" gap="3">
                                <ClientIcon label="MCP" />
                                <Box>
                                    <Text
                                        weight="medium"
                                        size="2"
                                        style={{
                                            display: "block",
                                        }}
                                    >
                                        Any MCP client
                                    </Text>
                                    <Text as="div" size="1" color="gray">
                                        Cursor, VS Code, custom agents
                                    </Text>
                                </Box>
                            </Flex>
                            <Text
                                size="2"
                                color="gray"
                                style={{
                                    flex: "0 0 auto",
                                }}
                            >
                                {universalClientOpen ? "Hide" : "Endpoint"}
                            </Text>
                        </Flex>
                    </button>

                    {universalClientOpen && (
                        <Flex
                            direction="column"
                            gap="4"
                            px="5"
                            py="4"
                            style={{
                                borderTop: "1px solid var(--dc-card-border)",
                                backgroundColor: "var(--dc-raise-bg)",
                            }}
                        >
                            <Flex align="center" justify="between" gap="3">
                                <Text weight="bold" size="2">
                                    Connect any MCP client
                                </Text>
                                <Button size="1" variant="ghost" color="gray" onClick={() => setUniversalClientOpen(false)}>
                                    Close
                                </Button>
                            </Flex>

                            {[
                                [
                                    "1",
                                    "Any MCP-compatible client",
                                    "Register the endpoint as a remote MCP server with OAuth.",
                                ],
                                [
                                    "2",
                                    "Authorize once",
                                    "Tokens are scoped per client and can be revoked from Settings.",
                                ],
                            ].map(([number, title, body]) => (
                                <Flex key={number} gap="3" align="start">
                                    <Flex
                                        align="center"
                                        justify="center"
                                        aria-hidden="true"
                                        style={{
                                            width: 24,
                                            height: 24,
                                            flex: "0 0 24px",
                                            borderRadius: "50%",
                                            backgroundColor: "var(--dc-card-bg)",
                                            color: "var(--dc-muted)",
                                            fontFamily: "var(--dc-font-mono)",
                                            fontSize: 12,
                                        }}
                                    >
                                        {number}
                                    </Flex>
                                    <Box>
                                        <Text
                                            weight="medium"
                                            size="2"
                                            style={{
                                                display: "block",
                                                marginBottom: 3,
                                            }}
                                        >
                                            {title}
                                        </Text>
                                        <Text as="div" size="2" color="gray">
                                            {body}
                                        </Text>
                                    </Box>
                                </Flex>
                            ))}

                            <Flex className="dc-endpoint-row" gap="2" align="center">
                                <Box
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        padding: "11px 13px",
                                        border: "1px solid var(--dc-card-border)",
                                        borderRadius: 10,
                                        backgroundColor: "var(--dc-bg)",
                                        overflowX: "auto",
                                        ...MONO_STYLE,
                                    }}
                                >
                                    <Text
                                        size="1"
                                        color="gray"
                                        style={{
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {MCP_CONNECTOR_URL}
                                    </Text>
                                </Box>
                                <Button
                                    size="2"
                                    variant="surface"
                                    color="gray"
                                    style={{
                                        flex: "0 0 auto",
                                    }}
                                    onClick={() => copyToClipboard(MCP_CONNECTOR_URL, "Endpoint URL copied!")}
                                >
                                    Copy URL
                                </Button>
                            </Flex>
                        </Flex>
                    )}
                </Card>
            </Flex>
        </DashboardShell>
    );
};

export default DevicesPage;
